import React, { useState } from 'react';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { EducatorDto } from '../../../../api-service/clients/auto-generated-clients';
import { ChangeCourseEducatorsDialogComponent } from './change-course-educators-dialog-component';
import { CourseBulkEditResultDialogComponent } from './course-bulk-edit-result-dialog-component';
import { courseResultSummary, ICourseBulkEditResult, skippedCourseItems, skippedCoursesText } from './bulk-edit-results';
import { useCompanyEducators } from './use-company-educators';

export interface IChangeCourseEducatorsDialogProps {
  courseIds: string[];
  companyId: string;
  onCompleted?: () => void;
}

const attendanceClient = moduleClientFactory.createAttendanceClient();

export function ChangeCourseEducatorsDialog({ courseIds, companyId, onCompleted }: IChangeCourseEducatorsDialogProps) {
  const { educators, isLoadingEducators } = useCompanyEducators(companyId);
  const [isOpen, setIsOpen] = useState(true);
  const [educatorIdsToAdd, setEducatorIdsToAdd] = useState<string[]>([]);
  const [educatorIdsToRemove, setEducatorIdsToRemove] = useState<string[]>([]);
  const [includeFutureLessons, setIncludeFutureLessons] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [result, setResult] = useState<ICourseBulkEditResult>();

  function handleEducatorsToAddChange(chosen: EducatorDto[]) {
    setEducatorIdsToAdd(chosen.map(educator => educator.educatorId));
  }

  function handleEducatorsToRemoveChange(chosen: EducatorDto[]) {
    setEducatorIdsToRemove(chosen.map(educator => educator.educatorId));
  }

  function handleIncludeFutureLessonsToggle() {
    setIncludeFutureLessons(!includeFutureLessons);
  }

  async function handleConfirm() {
    setIsSaving(true);
    try {
      const response = await attendanceClient.addAndRemoveCourseEducators({ courseIds, educatorIdsToAdd, educatorIdsToRemove, includeFutureLessons });
      setResult(response);
    } catch {
      // The error message interceptor shows the reason, and the dialog stays open so the choice can be corrected
    } finally {
      setIsSaving(false);
    }
  }

  function handleClose() {
    setIsOpen(false);
  }

  function handleResultClose() {
    setIsOpen(false);
    onCompleted?.();
  }

  if (!isOpen) {
    return null;
  }

  if (result) {
    return (
      <CourseBulkEditResultDialogComponent
        title="Undervisere er rettet"
        summary={courseResultSummary(result, includeFutureLessons)}
        skippedItemsText={skippedCoursesText}
        skippedItems={skippedCourseItems(result)}
        onClose={handleResultClose}
      />
    );
  }

  return (
    <ChangeCourseEducatorsDialogComponent
      title={courseIds.length === 1 ? 'Ret undervisere på det valgte hold' : `Ret undervisere på de ${courseIds.length} valgte hold`}
      description="Holdene beholder deres undervisere. De valgte undervisere tilføjes eller fjernes."
      educators={educators}
      educatorIdsToAdd={educatorIdsToAdd}
      educatorIdsToRemove={educatorIdsToRemove}
      futureLessonsOption={{
        label: 'Ret også holdenes fremtidige lektioner',
        description: 'Kladder kommer med. Aflyste lektioner og lektioner med låst protokol ændres ikke.',
        checked: includeFutureLessons,
        onToggle: handleIncludeFutureLessonsToggle,
      }}
      isLoadingEducators={isLoadingEducators}
      isSaving={isSaving}
      onEducatorsToAddChange={handleEducatorsToAddChange}
      onEducatorsToRemoveChange={handleEducatorsToRemoveChange}
      onConfirm={handleConfirm}
      onClose={handleClose}
    />
  );
}
